import React, { useState, useEffect } from 'react';
import { Device, HealthMetricsHistory } from '../../types/deviceTelemetry';
import { deviceTelemetryService } from '../../services/deviceTelemetryService';
import HealthMetricsChart from './HealthMetricsChart';

interface DeviceDetailsProps {
  device: Device;
  onBack: () => void;
}

const DeviceDetails: React.FC<DeviceDetailsProps> = ({ device, onBack }) => {
  const [healthHistory, setHealthHistory] = useState<HealthMetricsHistory | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('24h');
  const [loading, setLoading] = useState(true);
  const [sendingCommand, setSendingCommand] = useState<string | null>(null);

  // Load health history
  useEffect(() => {
    const loadHealthHistory = async () => {
      try {
        setLoading(true);
        // Try API first, fall back to mock data
        try {
          const response = await deviceTelemetryService.getDeviceHealthHistory(device.deviceId, selectedPeriod);
          if (response.success) {
            setHealthHistory(response.data);
          } else {
            throw new Error('API failed');
          }
        } catch (error) {
          // Fallback to mock data
          const mockResponse = deviceTelemetryService.getMockHealthHistory(device.deviceId, selectedPeriod);
          setHealthHistory(mockResponse.data);
        }
      } catch (error) {
        console.error('Failed to load health history:', error);
      } finally {
        setLoading(false);
      }
    };

    loadHealthHistory();
  }, [device.deviceId, selectedPeriod]);

  // Send device command
  const sendCommand = async (command: string, parameters?: Record<string, any>) => {
    try {
      setSendingCommand(command);
      const response = await deviceTelemetryService.sendDeviceCommand(device.deviceId, command, parameters);
      
      if (response.success) {
        window.alert(`Command "${command}" sent successfully to ${device.deviceName}`);
      } else {
        window.alert(`Failed to send command: ${response.error}`);
      }
    } catch (error) {
      window.alert(`Failed to send command: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSendingCommand(null);
    }
  };

  // Acknowledge alert
  const acknowledgeAlert = async (alertId: string) => {
    try {
      await deviceTelemetryService.acknowledgeAlert(device.deviceId, alertId);
      window.alert('Alert acknowledged');
    } catch (error) {
      window.alert(`Failed to acknowledge alert: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      online: { color: '#22c55e', text: 'Online', icon: '🟢' },
      offline: { color: '#6b7280', text: 'Offline', icon: '🔴' },
      error: { color: '#ef4444', text: 'Error', icon: '⚠️' },
      maintenance: { color: '#3b82f6', text: 'Maintenance', icon: '🔧' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.offline;
    
    return (
      <span className="status-badge large" style={{ backgroundColor: config.color }}>
        {config.icon} {config.text}
      </span>
    );
  };

  // Get health score color
  const getHealthScoreColor = (score: number): string => {
    if (score >= 90) return '#22c55e';
    if (score >= 70) return '#eab308';
    if (score >= 50) return '#f97316';
    return '#ef4444';
  };

  // Format uptime
  const formatUptime = (uptimeSeconds: number) => {
    const days = Math.floor(uptimeSeconds / 86400);
    const hours = Math.floor((uptimeSeconds % 86400) / 3600);
    const minutes = Math.floor((uptimeSeconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <div className="device-details">
      {/* Header */}
      <div className="device-details-header">
        <button onClick={onBack} className="back-button">
          ← Back to Device List
        </button>
        <div className="device-title">
          <h2>{device.deviceName}</h2>
          <div className="device-subtitle">
            {device.deviceType} • {device.storeName} • {device.enterpriseName}
          </div>
        </div>
        <div className="device-status">
          {getStatusBadge(device.status)}
        </div>
      </div>

      {/* Main Content */}
      <div className="device-details-content">
        {/* Left Column - Device Info & Metrics */}
        <div className="device-details-left">
          {/* Device Information */}
          <div className="info-section">
            <h3>Device Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Device ID</label>
                <span>{device.deviceId}</span>
              </div>
              <div className="info-item">
                <label>Device Type</label>
                <span>{device.deviceType}</span>
              </div>
              <div className="info-item">
                <label>Model</label>
                <span>{device.configuration.model}</span>
              </div>
              <div className="info-item">
                <label>OS Version</label>
                <span>{device.configuration.osVersion}</span>
              </div>
              <div className="info-item">
                <label>App Version</label>
                <span>{device.configuration.appVersion}</span>
              </div>
              <div className="info-item">
                <label>IP Address</label>
                <span>{device.ipAddress || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>MAC Address</label>
                <span>{device.macAddress || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Install Date</label>
                <span>{new Date(device.configuration.installDate).toLocaleDateString()}</span>
              </div>
              <div className="info-item">
                <label>Last Config Update</label>
                <span>{new Date(device.configuration.lastConfigUpdate).toLocaleDateString()}</span>
              </div>
              <div className="info-item">
                <label>Last Seen</label>
                <span>{new Date(device.lastSeen).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Hardware Specifications */}
          <div className="info-section">
            <h3>Hardware Specifications</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>CPU</label>
                <span>{device.configuration.hardwareSpecs.cpu}</span>
              </div>
              <div className="info-item">
                <label>Memory</label>
                <span>{device.configuration.hardwareSpecs.memory}</span>
              </div>
              <div className="info-item">
                <label>Storage</label>
                <span>{device.configuration.hardwareSpecs.storage}</span>
              </div>
              <div className="info-item">
                <label>Display</label>
                <span>{device.configuration.hardwareSpecs.display}</span>
              </div>
            </div>
          </div>

          {/* Current Metrics */}
          <div className="info-section">
            <h3>Current Metrics</h3>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">💚</div>
                <div className="metric-content">
                  <div className="metric-value" style={{ color: getHealthScoreColor(device.healthScore) }}>
                    {device.healthScore}%
                  </div>
                  <div className="metric-label">Health Score</div>
                </div>
              </div>

              {device.currentMetrics.batteryLevel !== undefined && (
                <div className="metric-card">
                  <div className="metric-icon">🔋</div>
                  <div className="metric-content">
                    <div className="metric-value">{device.currentMetrics.batteryLevel}%</div>
                    <div className="metric-label">Battery</div>
                  </div>
                </div>
              )}

              {device.currentMetrics.memoryUsage !== undefined && (
                <div className="metric-card">
                  <div className="metric-icon">💾</div>
                  <div className="metric-content">
                    <div className="metric-value">{device.currentMetrics.memoryUsage}%</div>
                    <div className="metric-label">Memory</div>
                  </div>
                </div>
              )}

              {device.currentMetrics.cpuUsage !== undefined && (
                <div className="metric-card">
                  <div className="metric-icon">⚡</div>
                  <div className="metric-content">
                    <div className="metric-value">{device.currentMetrics.cpuUsage}%</div>
                    <div className="metric-label">CPU</div>
                  </div>
                </div>
              )}

              {device.currentMetrics.diskUsage !== undefined && (
                <div className="metric-card">
                  <div className="metric-icon">💿</div>
                  <div className="metric-content">
                    <div className="metric-value">{device.currentMetrics.diskUsage}%</div>
                    <div className="metric-label">Disk</div>
                  </div>
                </div>
              )}

              {device.currentMetrics.networkSignal !== undefined && (
                <div className="metric-card">
                  <div className="metric-icon">📶</div>
                  <div className="metric-content">
                    <div className="metric-value">{device.currentMetrics.networkSignal}%</div>
                    <div className="metric-label">Network</div>
                  </div>
                </div>
              )}

              {device.currentMetrics.temperature !== undefined && (
                <div className="metric-card">
                  <div className="metric-icon">🌡️</div>
                  <div className="metric-content">
                    <div className="metric-value">{device.currentMetrics.temperature}°C</div>
                    <div className="metric-label">Temperature</div>
                  </div>
                </div>
              )}

              {device.currentMetrics.uptime !== undefined && (
                <div className="metric-card">
                  <div className="metric-icon">⏱️</div>
                  <div className="metric-content">
                    <div className="metric-value">{formatUptime(device.currentMetrics.uptime)}</div>
                    <div className="metric-label">Uptime</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Device Commands */}
          <div className="info-section">
            <h3>Device Commands</h3>
            <div className="command-buttons">
              <button
                onClick={() => sendCommand('restart')}
                disabled={sendingCommand !== null || device.status === 'offline'}
                className="command-button restart"
              >
                {sendingCommand === 'restart' ? 'Restarting...' : '🔄 Restart Device'}
              </button>
              
              <button
                onClick={() => sendCommand('diagnostic')}
                disabled={sendingCommand !== null || device.status === 'offline'}
                className="command-button diagnostic"
              >
                {sendingCommand === 'diagnostic' ? 'Running...' : '🔍 Run Diagnostic'}
              </button>
              
              <button
                onClick={() => sendCommand('update')}
                disabled={sendingCommand !== null || device.status === 'offline'}
                className="command-button update"
              >
                {sendingCommand === 'update' ? 'Updating...' : '⬆️ Check for Updates'}
              </button>
              
              <button
                onClick={() => sendCommand('maintenance')}
                disabled={sendingCommand !== null}
                className="command-button maintenance"
              >
                {sendingCommand === 'maintenance' ? 'Setting...' : '🔧 Maintenance Mode'}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Charts & Alerts */}
        <div className="device-details-right">
          {/* Health Metrics Chart */}
          <div className="chart-section">
            <div className="chart-header">
              <h3>Health Metrics History</h3>
              <div className="period-selector">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                >
                  <option value="1h">Last Hour</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
              </div>
            </div>
            
            {loading ? (
              <div className="chart-loading">
                <div className="loading-spinner-small"></div>
                Loading metrics...
              </div>
            ) : healthHistory ? (
              <HealthMetricsChart
                data={healthHistory}
                deviceName={device.deviceName}
              />
            ) : (
              <div className="chart-error">
                Failed to load metrics data
              </div>
            )}
          </div>

          {/* Active Alerts */}
          <div className="alerts-section">
            <h3>Active Alerts ({device.alerts.length})</h3>
            
            {device.alerts.length === 0 ? (
              <div className="no-alerts">
                ✅ No active alerts for this device
              </div>
            ) : (
              <div className="alerts-list">
                {device.alerts.map(alert => (
                  <div key={alert.id} className={`alert-item ${alert.type} ${alert.acknowledged ? 'acknowledged' : ''}`}>
                    <div className="alert-header">
                      <div className="alert-type">
                        {alert.type === 'critical' ? '🚨' : alert.type === 'warning' ? '⚠️' : 'ℹ️'}
                        <span className={`alert-type-label ${alert.type}`}>
                          {alert.type.toUpperCase()}
                        </span>
                      </div>
                      <div className="alert-time">
                        {new Date(alert.timestamp).toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="alert-content">
                      <div className="alert-title">{alert.title}</div>
                      <div className="alert-message">{alert.message}</div>
                    </div>
                    
                    {!alert.acknowledged && (
                      <div className="alert-actions">
                        <button
                          onClick={() => acknowledgeAlert(alert.id)}
                          className="acknowledge-button"
                        >
                          Acknowledge
                        </button>
                      </div>
                    )}
                    
                    {alert.acknowledged && (
                      <div className="alert-acknowledged">
                        ✅ Acknowledged
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceDetails;